import React from 'react';
import styles from './loader.module.css';
import s from './skeleton.module.css';

type TSkeletonProps = {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
};

/** Мерцающая заглушка вместо блока, который ещё грузится. */
const Skeleton: React.FC<TSkeletonProps> = ({ width = '100%', height = 14, radius, className }) => (
  <span
    className={[styles.skeleton, className ?? ''].join(' ').trim()}
    style={{ display: 'block', width, height, borderRadius: radius }}
    aria-hidden="true"
  />
);

export default Skeleton;

/* ═══ Готовые скелетоны панели ═══════════════════════════════════════
   Повторяют форму настоящего содержимого (строки 60px как в DataTable,
   карточки списков, шапку профиля), поэтому при приходе данных ничего
   не сдвигается. Каждая группа проявляется через 150 мс — быстрые ответы
   не мигают заглушкой. */

/** Ширины плашек «как у текста» — чуть разные от строки к строке */
const WIDTHS = ['72%', '58%', '84%', '64%', '46%', '78%', '52%', '68%'];
const w = (i: number) => WIDTHS[i % WIDTHS.length];

/** Плашка-«строка текста» */
export const SkeletonBar: React.FC<{ width?: number | string; height?: number; radius?: number | string; circle?: boolean }> = ({
  width = '100%', height = 12, radius, circle,
}) => (
  <span
    className={circle ? `${s.bar} ${s.circle}` : s.bar}
    style={{ width, height, borderRadius: radius }}
    aria-hidden="true"
  />
);

/** Обёртка группы: aria-busy + задержка появления */
const Group: React.FC<{ className?: string; style?: React.CSSProperties; label?: string; children: React.ReactNode }> = ({
  className, style, label = 'Загрузка', children,
}) => (
  <div className={className ? `${s.group} ${className}` : s.group} style={style} role="status" aria-busy="true" aria-label={label}>
    {children}
  </div>
);

/** Карточка: заголовок и несколько строк текста (виджеты, блоки настроек) */
export const SkeletonCard: React.FC<{
  lines?: number;
  title?: boolean;
  /** Фиксированная высота — когда настоящая карточка известной высоты */
  height?: number | string;
  /** Без собственной рамки — внутри уже нарисованной карточки */
  bare?: boolean;
  className?: string;
}> = ({ lines = 3, title = true, height, bare = false, className }) => (
  <Group
    className={[bare ? s.cardBare : s.card, className ?? ''].filter(Boolean).join(' ')}
    style={height !== undefined ? { height } : undefined}
  >
    {title && <SkeletonBar width={140} height={11} />}
    <div className={s.cardLines}>
      {Array.from({ length: lines }, (_, i) => <SkeletonBar key={i} width={w(i)} />)}
    </div>
  </Group>
);

/**
 * Таблица в рамке DataTable: шапка 37px и строки по 60px.
 * columns — число колонок или ширины колонок для grid (например ['70px', '2fr', '1fr']).
 */
export const SkeletonTable: React.FC<{
  columns?: number | string[];
  rows?: number;
  header?: boolean;
  /** Без собственной рамки — внутри уже нарисованной карточки */
  bare?: boolean;
  /** Размеры под конкретную таблицу раздела (по умолчанию — как DataTable) */
  rowHeight?: number;
  headHeight?: number;
  gap?: number;
  className?: string;
}> = ({ columns = 5, rows = 8, header = true, bare = false, rowHeight = 60, headHeight = 37, gap = 32, className }) => {
  const cols = typeof columns === 'number' ? Array.from({ length: columns }, (_, i) => (i === 1 ? '2fr' : '1fr')) : columns;
  const style = { '--cols': cols.join(' '), '--gap': `${gap}px`, height: rowHeight } as React.CSSProperties;
  const headStyle = { ...style, height: headHeight };
  return (
    <Group className={[s.table, bare ? s.listBare : '', className ?? ''].filter(Boolean).join(' ')}>
      {header && (
        <div className={`${s.tableRow} ${s.tableHead}`} style={headStyle}>
          {cols.map((_, c) => <SkeletonBar key={c} width={c === 1 ? 90 : 56} />)}
        </div>
      )}
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className={s.tableRow} style={style}>
          {cols.map((_, c) => <SkeletonBar key={c} width={c === 0 ? '50%' : w(r + c)} />)}
        </div>
      ))}
    </Group>
  );
};

/**
 * Строки-заглушки внутри уже нарисованной таблицы (<tbody>): шапка
 * остаётся настоящей, высота строк — как у данных.
 */
export const SkeletonRows: React.FC<{ columns: number; rows?: number; rowHeight?: number }> = ({ columns, rows = 8, rowHeight = 60 }) => (
  <>
    {Array.from({ length: rows }, (_, r) => (
      <tr key={`skel-${r}`} className={s.group} aria-hidden="true">
        {Array.from({ length: columns }, (_, c) => (
          <td key={c} className={s.td} style={{ height: rowHeight }}>
            <SkeletonBar width={c === 0 ? '55%' : w(r + c)} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/** Карточки списка на телефоне (пользователи, обращения, логи) */
export const SkeletonCardList: React.FC<{
  count?: number;
  /** Строк под заголовком карточки */
  lines?: number;
  /** Пилюля статуса справа в заголовке */
  pill?: boolean;
  /** Высота карточки, если у настоящих она фиксирована */
  cardHeight?: number;
  className?: string;
}> = ({ count = 6, lines = 2, pill = true, cardHeight, className }) => (
  <Group className={className ? `${s.cardList} ${className}` : s.cardList}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className={s.listCard} style={cardHeight ? { height: cardHeight } : undefined}>
        <div className={s.listCardTop}>
          <SkeletonBar width={w(i)} height={14} />
          {pill && <SkeletonBar width={72} height={22} radius={11} />}
        </div>
        {Array.from({ length: lines }, (_, l) => <SkeletonBar key={l} width={l === 0 ? '62%' : '40%'} height={11} />)}
      </div>
    ))}
  </Group>
);

/** Список строк в карточке (новости, страницы, FAQ, обращения, очередь поддержки) */
export const SkeletonList: React.FC<{
  rows?: number;
  rowHeight?: number;
  /** Квадрат/кружок слева (аватар, иконка) */
  avatar?: boolean | 'circle';
  /** Вторая строка под заголовком */
  meta?: boolean;
  /** Плашка справа (дата, статус) */
  aside?: boolean;
  /** Без собственной рамки — внутри уже нарисованной карточки */
  bare?: boolean;
  className?: string;
}> = ({ rows = 6, rowHeight = 64, avatar = false, meta = true, aside = true, bare = false, className }) => (
  <Group className={[s.list, bare ? s.listBare : '', className ?? ''].filter(Boolean).join(' ')}>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className={s.listRow} style={{ height: rowHeight }}>
        {avatar && <SkeletonBar width={36} height={36} radius={avatar === 'circle' ? '50%' : 10} />}
        <div className={s.listRowText}>
          <SkeletonBar width={w(i)} height={13} />
          {meta && <SkeletonBar width={w(i + 3)} height={10} />}
        </div>
        {aside && <SkeletonBar width={64} height={11} />}
      </div>
    ))}
  </Group>
);

/** Колонки доски задач с карточками */
export const SkeletonBoard: React.FC<{ columns?: number; cards?: number[] }> = ({ columns = 4, cards = [3, 2, 1, 2] }) => (
  <Group className={s.board} style={{ '--n': columns } as React.CSSProperties}>
    {Array.from({ length: columns }, (_, c) => (
      <div key={c} className={s.boardCol}>
        <SkeletonBar width="50%" height={11} />
        {Array.from({ length: cards[c % cards.length] }, (_, i) => (
          <div key={i} className={s.listCard}>
            <SkeletonBar width={w(c + i)} height={13} />
            <SkeletonBar width="38%" height={10} />
          </div>
        ))}
      </div>
    ))}
  </Group>
);

/**
 * Шапка профиля и карточки под ней.
 *  • admin  — карточка пользователя в админке: шапка с аватаром и действиями,
 *             четыре плитки цифр, вкладки, две колонки карточек;
 *  • player — профиль игрока: аватар, имя, пилюли, вкладки, две карточки.
 */
export const SkeletonProfile: React.FC<{ variant?: 'admin' | 'player'; className?: string }> = ({ variant = 'admin', className }) => {
  if (variant === 'player') {
    return (
      <Group className={className ? `${s.profile} ${className}` : s.profile}>
        <div className={s.playerHead}>
          <SkeletonBar width={84} height={84} radius={22} />
          <div className={s.profileHeadText}>
            <SkeletonBar width={200} height={22} />
            <div className={s.pills}><SkeletonBar width={110} height={24} radius={12} /><SkeletonBar width={140} height={24} radius={12} /></div>
          </div>
        </div>
        <div className={s.tabs}>{[70, 110, 100, 100].map((x, i) => <SkeletonBar key={i} width={x} height={36} radius={18} />)}</div>
        <div className={`${s.cols} ${s.colsEven}`}>
          <SkeletonCard lines={4} />
          <SkeletonCard lines={4} />
        </div>
      </Group>
    );
  }
  return (
    <Group className={className ? `${s.profile} ${className}` : s.profile}>
      <div className={s.profileHead}>
        <SkeletonBar width={72} height={72} radius={18} />
        <div className={s.profileHeadText}>
          <SkeletonBar width={220} height={22} />
          <div className={s.pills}>{[78, 92, 96].map((x, i) => <SkeletonBar key={i} width={x} height={24} radius={12} />)}</div>
        </div>
        <div className={s.profileActions}>{[120, 120, 40].map((x, i) => <SkeletonBar key={i} width={x} height={40} radius={10} />)}</div>
      </div>
      <div className={s.stats}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={s.stat}><SkeletonBar width="55%" height={16} /><SkeletonBar width="75%" height={10} /></div>
        ))}
      </div>
      <div className={s.tabs}>{[70, 100, 110, 120, 100].map((x, i) => <SkeletonBar key={i} width={x} height={38} radius={10} />)}</div>
      <div className={s.cols}>
        <div className={s.col}><SkeletonCard lines={6} /><SkeletonCard lines={2} /></div>
        <div className={s.col}><SkeletonCard lines={3} /><SkeletonCard lines={4} /></div>
      </div>
    </Group>
  );
};

/** Заглушка области содержимого панели, пока грузится код раздела */
export const SkeletonPage: React.FC = () => (
  <Group className={s.profile} label="Загрузка раздела">
    <div className={s.pageHead}>
      <SkeletonBar width={220} height={30} radius={8} />
      <SkeletonBar width={320} height={11} />
    </div>
    <div className={s.tabs}>{[90, 110, 100].map((x, i) => <SkeletonBar key={i} width={x} height={36} radius={10} />)}</div>
    <SkeletonTable rows={6} />
  </Group>
);

/** Плитки цифр (KPI) и, по желанию, карточка графика под ними */
export const SkeletonStats: React.FC<{ tiles?: number; chartHeight?: number }> = ({ tiles = 4, chartHeight }) => (
  <Group className={s.profile}>
    <div className={s.stats} style={{ '--tiles': tiles } as React.CSSProperties}>
      {Array.from({ length: tiles }, (_, i) => (
        <div key={i} className={s.stat}><SkeletonBar width="45%" height={10} /><SkeletonBar width="60%" height={18} /></div>
      ))}
    </div>
    {chartHeight !== undefined && (
      <div className={s.card} style={{ height: chartHeight }}>
        <SkeletonBar width={160} height={11} />
        <SkeletonBar width="100%" height={chartHeight - 70} radius={10} />
      </div>
    )}
  </Group>
);

/** Переписка: шапка обращения и пузыри сообщений по обе стороны */
export const SkeletonThread: React.FC<{ messages?: number; header?: boolean }> = ({ messages = 4, header = true }) => (
  <Group className={s.profile} label="Загрузка переписки">
    {header && (
      <div className={s.card}>
        <SkeletonBar width={260} height={20} />
        <div className={s.pills}><SkeletonBar width={90} height={24} radius={12} /><SkeletonBar width={120} height={24} radius={12} /></div>
      </div>
    )}
    <div className={s.thread}>
      {Array.from({ length: messages }, (_, i) => (
        <div key={i} className={i % 2 ? `${s.bubble} ${s.bubbleMine}` : s.bubble}>
          <SkeletonBar width={90} height={10} />
          <SkeletonBar width={w(i)} height={12} />
          {i % 3 !== 2 && <SkeletonBar width={w(i + 2)} height={12} />}
        </div>
      ))}
    </div>
  </Group>
);
